import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { complianceRulesApi, type ComplianceRulesListParams } from '../api/complianceRules';
import { ApiError } from '../api/client';
import type {
  ComplianceRule,
  ComplianceRuleCreateInput,
  ComplianceRuleUpdateInput,
  Page,
  RecomputeReport,
  UUID,
} from '../api/types';

export const complianceRulesKeys = {
  all:    ['compliance-rules'] as const,
  list:   (p: ComplianceRulesListParams) => ['compliance-rules', 'list', p] as const,
  detail: (id: UUID) => ['compliance-rules', 'detail', id] as const,
};

export function useComplianceRules(params: ComplianceRulesListParams = {}) {
  return useQuery<Page<ComplianceRule>, ApiError>({
    queryKey: complianceRulesKeys.list(params),
    queryFn: () => complianceRulesApi.list(params),
  });
}

export function useComplianceRule(id: UUID | undefined) {
  return useQuery<ComplianceRule, ApiError>({
    queryKey: complianceRulesKeys.detail(id ?? ''),
    queryFn: () => complianceRulesApi.get(id!),
    enabled: !!id,
  });
}

function invalidateDownstream(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: complianceRulesKeys.all,    refetchType: 'all' });
  qc.invalidateQueries({ queryKey: ['agreements'],             refetchType: 'all' });
  qc.invalidateQueries({ queryKey: ['person-reviews'],         refetchType: 'all' });
  qc.invalidateQueries({ queryKey: ['ref'],                    refetchType: 'all' });
}

export function useCreateComplianceRule() {
  const qc = useQueryClient();
  return useMutation<RecomputeReport, ApiError, ComplianceRuleCreateInput>({
    mutationFn: complianceRulesApi.create,
    onSuccess: () => invalidateDownstream(qc),
  });
}

export function useUpdateComplianceRule(id: UUID) {
  const qc = useQueryClient();
  return useMutation<RecomputeReport, ApiError, ComplianceRuleUpdateInput>({
    mutationFn: (p) => complianceRulesApi.update(id, p),
    onSuccess: () => invalidateDownstream(qc),
  });
}

export function useDeleteComplianceRule() {
  const qc = useQueryClient();
  return useMutation<RecomputeReport, ApiError, UUID>({
    mutationFn: complianceRulesApi.delete,
    onSuccess: () => invalidateDownstream(qc),
  });
}

export function useRestoreComplianceRule() {
  const qc = useQueryClient();
  return useMutation<RecomputeReport, ApiError, UUID>({
    mutationFn: complianceRulesApi.restore,
    onSuccess: () => invalidateDownstream(qc),
  });
}
